import { useState, useEffect, useRef } from 'react'
import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { CalendarDays, ClipboardList, LayoutDashboard, LogOut, ShieldCheck } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import FeedbackWidget from './FeedbackWidget'
import { ADMIN_SECTIONS, adminSectionFrom } from './adminSections'
import 'bootstrap-icons/font/bootstrap-icons.css'

const logoMark = process.env.PUBLIC_URL + '/images/K_logo.jpeg'

// Sidebar mirrors the JNJ icon rail (gradient-stroked icons, permanent label underneath,
// left accent bar for the active entry) in KBL's premium orange -> amber palette,
// the same stops as the Sign In button.
const ICON_GRADIENT_ID = 'kbl-icon-gradient'
const PRIMARY = '#ea580c'
// Deeper orange for the 11px labels so they stay readable on white
const LABEL = '#c2410c'

// userSpaceOnUse (each icon's own 24x24 space) so straight strokes still get painted
const IconGradientDefs = () => (
  <svg aria-hidden="true" focusable="false" width="0" height="0" className="absolute">
    <defs>
      <linearGradient id={ICON_GRADIENT_ID} gradientUnits="userSpaceOnUse" x1="3" y1="22" x2="21" y2="2">
        <stop offset="0%" stopColor="#ea580c" />
        <stop offset="48%" stopColor="#f97316" />
        <stop offset="100%" stopColor="#fbbf24" />
      </linearGradient>
    </defs>
  </svg>
)

const NavItem = ({ icon: Icon, label, isActive, onClick }) => (
  <button type="button" onClick={onClick} className="block w-full" aria-current={isActive ? 'page' : undefined}>
    <span className="relative flex w-full flex-col items-center gap-0.5 rounded-xl px-1 py-1.5 transition-colors hover:bg-[#ffedd5]/60">
      {isActive && (
        <span
          aria-hidden
          className="absolute top-1/2 -left-1 h-8 w-1 -translate-y-1/2 rounded-r-full"
          style={{ backgroundColor: PRIMARY }}
        />
      )}
      <span className="relative flex h-10 w-11 shrink-0 items-center justify-center">
        <Icon
          className={`h-10 w-10 transition-transform duration-200 ${isActive ? 'scale-105' : ''}`}
          strokeWidth={1.9}
          style={{ stroke: `url(#${ICON_GRADIENT_ID})` }}
        />
      </span>
      <span
        className={`max-w-full truncate text-[11px] leading-tight ${isActive ? 'font-semibold' : 'font-medium'}`}
        style={{ color: LABEL }}
      >
        {label}
      </span>
    </span>
  </button>
)

// Second-level rail shown next to the main one while on /admin (desktop only;
// the Admin page shows the same sections as a horizontal picker on small screens)
const AdminSubSidebar = ({ activeSection, onSelect }) => (
  <aside className="print-hidden fixed inset-y-0 left-20 z-40 hidden w-44 flex-col border-r border-[#efe3d6] bg-[#fffcf9] md:flex">
    <div className="flex h-16 shrink-0 flex-col justify-center px-4">
      <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Management</span>
      <span className="text-premium text-base font-bold leading-tight">Admin Panel</span>
    </div>
    <nav className="flex flex-1 flex-col gap-1 overflow-y-auto border-t border-[#efe3d6] px-2 py-3">
      {ADMIN_SECTIONS.map(({ id, label, icon: Icon }) => {
        const isActive = id === activeSection
        return (
          <button
            key={id}
            type="button"
            onClick={() => onSelect(id)}
            aria-current={isActive ? 'page' : undefined}
            className={`relative flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-left text-sm transition-colors ${
              isActive ? 'bg-[#fff7ed] font-semibold text-[#9a3412]' : 'font-medium text-slate-700 hover:bg-[#ffedd5]/60'
            }`}
          >
            {isActive && (
              <span
                aria-hidden
                className="absolute top-1/2 -left-2 h-7 w-1 -translate-y-1/2 rounded-r-full"
                style={{ backgroundColor: PRIMARY }}
              />
            )}
            <Icon className="h-5 w-5 shrink-0" strokeWidth={1.9} style={{ stroke: `url(#${ICON_GRADIENT_ID})` }} />
            {label}
          </button>
        )
      })}
    </nav>
  </aside>
)

const initialsFor = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?'

const Layout = () => {
  const { logout, isStaff, isSuperuser, isClientuser, user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const userMenuRef = useRef(null)

  useEffect(() => {
    if (!showUserMenu) return
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) setShowUserMenu(false)
    }
    const handleEscape = (event) => {
      if (event.key === 'Escape') setShowUserMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [showUserMenu])

  const isActive = (path) => {
    // Exact match
    if (location.pathname === path) return true

    // Special handling for /surveys to exclude /surveys/new
    if (path === '/surveys') {
      // Active for /surveys or /surveys/:id/edit, /surveys/:id/invoice, etc.
      // But NOT for /surveys/new
      return location.pathname.startsWith('/surveys/') &&
             !location.pathname.startsWith('/surveys/new')
    }

    // For other paths, check if it starts with path + '/'
    return location.pathname.startsWith(path + '/')
  }

  // Clicking the page you're already on reloads it to refresh its data. Only an exact
  // match reloads: from a sub-page like /surveys/:id/invoice the button navigates back
  // to the list, even though it's still highlighted as the active section.
  const goTo = (path) => {
    if (location.pathname === path) {
      window.location.reload()
    } else {
      navigate(path)
    }
  }

  const goHome = () => {
    if (location.pathname === '/') {
      window.location.reload()
    } else {
      navigate('/')
    }
  }

  const navItems = [
    { path: '/surveys', label: 'Surveys', icon: ClipboardList, visible: isStaff },
    { path: '/operations', label: 'Operations', icon: CalendarDays, visible: true },
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, visible: isStaff || isClientuser },
  ].filter((item) => item.visible)

  const showAdmin = (isStaff || isClientuser) && isSuperuser
  const showAdminSubSidebar = showAdmin && isActive('/admin')

  const displayName = user
    ? user.welcome_name ||
      `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
      user.username
    : ''

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50/40 to-white">
      <aside className="print-hidden fixed inset-y-0 left-0 z-50 flex w-20 flex-col border-r border-[#efe3d6] bg-white text-[#1c1410]">
        <IconGradientDefs />

        <div className="flex h-16 shrink-0 items-center justify-center px-1">
          <button
            type="button"
            onClick={goHome}
            aria-label="Home"
            className="flex h-[52px] w-[52px] items-center justify-center rounded-xl transition-transform hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ea580c]"
          >
            <img src={logoMark} alt="KBL Marine Surveys" className="h-11 w-11 object-contain" draggable="false" />
          </button>
        </div>

        <nav className="flex flex-1 flex-col gap-1.5 overflow-y-auto [scrollbar-width:none] border-t border-[#efe3d6] px-1 py-2">
          {navItems.map((item) => (
            <NavItem
              key={item.path}
              icon={item.icon}
              label={item.label}
              isActive={isActive(item.path)}
              onClick={() => goTo(item.path)}
            />
          ))}
        </nav>

        {showAdmin && (
          <div className="flex flex-col gap-0.5 border-t border-[#efe3d6] px-1 py-2">
            <NavItem icon={ShieldCheck} label="Admin" isActive={isActive('/admin')} onClick={() => goTo('/admin')} />
          </div>
        )}

        {user && (
          <div ref={userMenuRef} className="relative flex flex-col items-center gap-1 border-t border-[#efe3d6] p-2">
            <button
              type="button"
              onClick={() => setShowUserMenu((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={showUserMenu}
              title={displayName}
              className="flex w-full items-center justify-center rounded-lg py-1.5 transition-colors hover:bg-[#ffedd5]"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#fdf0e4] text-xs font-medium text-[#1c1410]">
                {initialsFor(displayName)}
              </span>
            </button>

            {showUserMenu && (
              <div
                role="menu"
                className="absolute bottom-2 left-full z-50 ml-3 w-56 rounded-lg border border-[#efe3d6] bg-white p-1 text-sm shadow-lg"
              >
                <div className="flex flex-col gap-0.5 px-2 py-1.5">
                  <span className="truncate font-medium">{displayName || 'Account'}</span>
                  {user.email && <span className="truncate text-xs text-[#78695c]">{user.email}</span>}
                </div>
                <div className="-mx-1 my-1 h-px bg-[#efe3d6]" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={logout}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4" />
                  Log out
                </button>
              </div>
            )}
          </div>
        )}
      </aside>

      {showAdminSubSidebar && (
        <AdminSubSidebar
          activeSection={adminSectionFrom(searchParams)}
          onSelect={(id) => navigate(`/admin?tab=${id}`)}
        />
      )}

      <div className={`pl-20 print:pl-0 ${showAdminSubSidebar ? 'md:pl-64' : ''}`}>
        <main>
          <Outlet />
        </main>
      </div>

      <FeedbackWidget />
    </div>
  )
}

export default Layout
